import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useMessageApi } from "../../shared/providers/MessageProvider";
import { showAuthStatusMessages } from "../../utils/auth0Utils";
import { Loader } from "../../shared";
import { getUserInfo, UserNotFoundError } from "../../services/userService";
import { USER_NOT_FOUND_FLAG } from "../../utils/constants";
import { 
  SUPER_ADMIN_ROLE_ID,
  hasPermission, 
  canViewAllCompanies
} from "../../utils/roleHelpers";
import { logoutWithFlag } from "../../helpers/authHelpers";
import Navbar from "../Navbar/Navbar";
import MerchantListView from "../MainView/MerchantListView";
import Incentive from "../Incentive/Incentive";
import Companies from "../Companies/Companies";
import Users from "../Users/Users";
import RolePermissions from "../RolePermissions/RolePermissions";
import { RateReviewDashboard } from "../RateReview";
import AdminBookingConfig from "../AdminBookingConfig/AdminBookingConfig";
import AdminBookingConfigRedirect from "../AdminBookingConfig/components/AdminBookingConfigRedirect/AdminBookingConfigRedirect";
import "./Dashboard.scss";

/**
 * Dashboard Component - Main container for authenticated users
 * Handles:
 *  - Auth verification and redirection
 *  - User info retrieval
 *  - Routing to various dashboard sections
 */
const Dashboard = () => {
  // Use Auth0 React SDK hook
  const { 
    isAuthenticated, 
    isLoading: auth0Loading, 
    user, 
    loginWithRedirect,
    logout
  } = useAuth0();

  // Message API from MessageProvider for toast notifications
  const message = useMessageApi();

  // Local state for user info, selected company, and loading status
  const [userInfo, setUserInfo] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // CSV Export data from MerchantListView
  const [csvExportData, setCsvExportData] = useState({
    merchants: [],
    headers: [],
    currentFilters: {},
    isLoading: false
  });

  // Show stored auth status messages (e.g., user not found) on mount
  useEffect(() => {
    showAuthStatusMessages(message);
  }, [message]);

  // Determine if the current user is an admin
  // Note: Since roles are dynamic, we check permissions instead of hardcoded role ID
  // Consider using permission-based checks (e.g., hasPermission(userInfo, 'users', 'view'))
  const isAdmin = useMemo(() => {
    // For backward compatibility, check if user has admin-like permissions
    // In future, replace with specific permission checks
    return userInfo?.permissions?.users?.view === true || 
           userInfo?.permissions?.dashboard?.view_all_companies === true;
  }, [userInfo]);
  
  // Determine if the current user is a super admin
  const isSuperAdmin = useMemo(() => userInfo?.role_id === SUPER_ADMIN_ROLE_ID, [userInfo]);

  // Extract permissions from userInfo
  const permissions = useMemo(() => userInfo?.permissions || {}, [userInfo]);

  // Check module access permissions via a single memoized map
  const {
    canViewDashboard,
    canViewCompanies,
    canViewIncentive,
    canViewUsers,
    canViewRolesAndPermissions,
    canViewRateReview,
    canViewAdminBookingConfig,
  } = useMemo(() => {
    if (!userInfo) {
      return {
        canViewDashboard: false,
        canViewCompanies: false,
        canViewIncentive: false,
        canViewUsers: false,
        canViewRolesAndPermissions: false,
        canViewRateReview: false,
        canViewAdminBookingConfig: false,
      };
    }

    return {
      canViewDashboard: hasPermission(userInfo, 'dashboard', 'view'),
      canViewCompanies: hasPermission(userInfo, 'companies', 'view'),
      canViewIncentive: hasPermission(userInfo, 'incentive', 'view'),
      canViewUsers: hasPermission(userInfo, 'users', 'view'),
      canViewRolesAndPermissions: hasPermission(userInfo, 'roles_and_permissions', 'view'),
      canViewRateReview: hasPermission(userInfo, 'rate_review_portal', 'view'),
      canViewAdminBookingConfig: hasPermission(userInfo, 'admin_booking_config', 'view'),
    };
  }, [userInfo]);
  
  // Check company visibility permissions
  const canViewAllCompaniesFlag = useMemo(() => 
    canViewAllCompanies(userInfo, 'dashboard'), 
    [userInfo]
  );

  // Memoized user identifier
  const userEmail = useMemo(() => user?.email || user?.name || "", [user]);

  // Logout helper to set local flag and redirect
  const handleLogoutWithFlag = useCallback(
    (flagKey, flagValue) => logoutWithFlag(logout, flagKey, flagValue),
    [logout]
  );

  // Effect: Check auth status and fetch user info on mount
  useEffect(() => {
    let isMounted = true;

    const initializeDashboard = async () => {
      // Wait for Auth0 to finish loading
      if (auth0Loading) {
        return;
      }

      // Redirect to login if not authenticated
      if (!isAuthenticated) {
        const returnTo = new URL(window.location).pathname;
        
        await loginWithRedirect({
          appState: {
            returnTo,
          },
        });
        return;
      }

      if (!userEmail) {
        message.error("Unable to retrieve user information.", 3);
        if (isMounted) {
          setIsLoading(false);
        }
        return;
      }

      if (isMounted) {
        setIsLoading(true);
      }

      try {
        const userData = await getUserInfo(userEmail);

        if (!userData) {
          message.error("Failed to load user data.", 3);
          return;
        }

        if (isMounted) {
          // Set user info and selected company
          setUserInfo({ ...userData, email: userEmail });
          setSelectedCompany(userData.company);
        }
      } catch (error) {
        // Handle "User not found" error - user exists in Auth0 but not in local database
        if (error instanceof UserNotFoundError) {
          handleLogoutWithFlag(USER_NOT_FOUND_FLAG, "true");
          return;
        }
        
        // Handle other errors
        console.error("Dashboard Initialization Error:", error);
        message.error("Unable to load dashboard. Try again.", 3);
      } finally {
        if (isMounted) {
          setIsLoading(false); // End loading regardless of success/failure
        }
      }
    };

    initializeDashboard();

    return () => {
      isMounted = false;
    };
  }, [auth0Loading, handleLogoutWithFlag, isAuthenticated, loginWithRedirect, message, userEmail]);

  /**
   * Handle company switch in a memoized callback
   */
  const handleCompanyChange = useCallback(
    (newCompany) => {
      // Check if user has permission to view all companies
      if (!canViewAllCompaniesFlag && newCompany !== userInfo?.company) {
        message.warning("You don't have permission to view other companies", 3);
        return;
      }

      if (newCompany !== selectedCompany) {
        setSelectedCompany(newCompany);
      }
    },
    [selectedCompany, canViewAllCompaniesFlag, userInfo, message]
  );

  /**
   * Handle CSV export data updates from MerchantListView
   */
  const handleCsvExportDataUpdate = useCallback(
    (exportData) => {
      setCsvExportData(exportData);
    },
    []
  );

  /**
   * Props for the MerchantListView - memoized for performance
   */
  const merchantListViewProps = useMemo(() => {
    return {
      userInfo: userInfo ? { ...userInfo, company: selectedCompany } : null,
      onCompanyChange: handleCompanyChange,
      isAdmin,
      onCsvExportDataUpdate: handleCsvExportDataUpdate,
      permissions: permissions,
    };
  }, [userInfo, selectedCompany, handleCompanyChange, isAdmin, handleCsvExportDataUpdate, permissions]);

  // Show loader while Auth0 is loading, checking authentication, or loading user data
  const shouldShowLoader = useMemo(
    () => auth0Loading || isLoading || !isAuthenticated,
    [auth0Loading, isAuthenticated, isLoading]
  );

  // Show loader while Auth0 is loading, checking authentication, or loading user data
  if (shouldShowLoader) {
    return <Loader isLoading={true} text="Loading dashboard..." />;
  }

  return (
    <div className="dashboard">
      {/* Top navigation bar */}
      <Navbar 
        isAdmin={isAdmin}
        isSuperAdmin={isSuperAdmin}
        company={selectedCompany} 
        onCompanySelect={handleCompanyChange}
        merchants={csvExportData.merchants}
        headers={csvExportData.headers}
        currentFilters={csvExportData.currentFilters}
        isLoading={csvExportData.isLoading}
        userInfo={userInfo}
        permissions={permissions}
        canViewAllCompanies={canViewAllCompaniesFlag}
      />

      {/* App routing logic */}
      <Routes>
        {/* Default: Merchant List - Check dashboard view permission */}
        {canViewDashboard && (
          <Route 
          path="/" 
          element={
              <MerchantListView {...merchantListViewProps} />
            } 
          />
        )}

        {/* Incentive Management - Check incentive view permission */}
        {canViewIncentive && (
          <Route 
          path="/incentive" 
          element={
              <Incentive currentCompany={selectedCompany} userInfo={userInfo} permissions={permissions} />
            } 
          />
        )}

        {/* Companies Management - Check companies view permission */}
        {canViewCompanies && (
          <Route 
            path="/companies" 
            element={
              <Companies 
                admin={isAdmin} 
                isSuperAdmin={isSuperAdmin}
                userInfo={userInfo}
                permissions={permissions}
              />
            } 
          />
        )}
        
        {/* Users Module - accessible to users with 'users' view permission */}

        {canViewUsers && (
          <Route 
            path="/users" 
            element={<Users userInfo={userInfo} isSuperAdmin={isSuperAdmin} />} 
          />
        )}

        {/* Role & Permission Management - accessible to users with roles_and_permissions.view permission */}
        {canViewRolesAndPermissions && (
          <Route 
            path="/roles" 
            element={<RolePermissions userInfo={userInfo} />} 
          />
        )}

        {/* Rate Review Dashboard - accessible to users with rate_review_portal.view permission */}
        {canViewRateReview && (
          <Route 
            path="/rate-review" 
            element={<RateReviewDashboard userInfo={userInfo} />} 
          />
        )}

        {/* Admin Booking & Routing Configuration - accessible to users with admin_booking_config.view permission */}
        <Route 
          path="/admin-booking-config" 
          element={
            canViewAdminBookingConfig ? (
              <AdminBookingConfig userInfo={userInfo} />
            ) : (
              <AdminBookingConfigRedirect message={message} />
            )
          } 
        />
      </Routes>
    </div>
  );
};

// Prevent re-rendering if props/state haven't changed
export default React.memo(Dashboard);